import {styled} from "@mui/material/styles";
import TableCell, {tableCellClasses} from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import React, {useCallback, useEffect, useState} from "react";
import Button from "@mui/material/Button";
import TableBody from "@mui/material/TableBody";
import InputLabel from "@mui/material/InputLabel";
import {InputAdornment, Select, TableFooter, TextField} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import SearchIcon from "@mui/icons-material/Search";
import {makeStyles} from "@mui/styles";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import {deleteCVById, findById, getCvDocument} from "../../utilities/cvAPI";
import keycloak from "../../Keycloak";
import Enums from "../General/Enums";
import Grid from "@mui/material/Grid";
import {modalButtonStyleDelete} from "../Jobs/TableTemplate";
import Modal from "@mui/material/Modal";
import {showSaveSuccessMessage} from "../toastUtils";

export function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

export function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

export function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export const buttonStyles = {
    color: 'black',
    '&:hover': {
        color: 'white',
        backgroundColor: 'Transparent'
    },
    width: '100%',
    height: '100%',
    justifyContent: 'flex-start',
    textTransform: 'none',
};

export const typographyStyles = {
    backgroundColor: '#F3F3F3',
    height: '50px',
    width: "180px",
    '&:hover': {
        backgroundColor: '#FF6600'
    },
};

const FlexContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const RightAlignFlexContainer = styled(FlexContainer)({
    justifyContent: 'flex-end',
});

const LeftAlignFlexContainer = styled(FlexContainer)({
    justifyContent: 'flex-start',
});

const FooterFlexContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
});

const LeftBottomFlexContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
});

const TopTableSearch = ({requestSearch, rowsPerPage, handleRowsPerPageChange, classes}) =>
    (<Box sx={{flexGrow: 1}} id="Table">

        <FlexContainer height={70}>
            <LeftAlignFlexContainer>
                <InputLabel id="rows-per-page-label" sx={{marginLeft: '16px'}}>
                    Show
                </InputLabel>
                <FormControl sx={{marginLeft: '15px', minWidth: '55px', marginRight: '7px'}}>
                    <Select
                        labelId="rows-per-page-label"
                        id="rows-per-page-select"
                        sx={{
                            height: '40px',
                            width: 'auto',
                            boxShadow: 'none',
                            borderRadius: "12px",
                            padding: "10px",
                            backgroundColor: "white",
                            '.MuiOutlinedInput-notchedOutline': {border: 0}
                        }}
                        label="Show rows per page"
                        disableUnderline={true}
                        variant={"standard"}
                        value={rowsPerPage}
                        data-testid={"candidatesDropdown"}
                        inputProps={{"data-testid": "candidatesDropdown-input"}}
                        onChange={handleRowsPerPageChange}
                    >
                        <MenuItem data-testid="menu-item-1" value={5}>5</MenuItem>
                        <MenuItem data-testid="menu-item-2" value={10}>10</MenuItem>
                        <MenuItem data-testid="menu-item-3" value={25}>25</MenuItem>
                    </Select>
                </FormControl>
                <InputLabel id="rows-per-page-label">entries</InputLabel>
            </LeftAlignFlexContainer>
            <RightAlignFlexContainer>
                <div className={"ticketSearchBar"}>
                    <TextField
                        data-testid="searchBarCandidates"
                        key={"password"}
                        placeholder="Search..."
                        type="search"
                        className={classes.root}
                        sx={{marginRight: '10px', backgroundColor: "white"}}
                        onChange={(e) => requestSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon/>
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>
            </RightAlignFlexContainer>
        </FlexContainer>
    </Box>)

export default function TableTemplate({columnsArray, data, search, filter, updatedCandidates}) {
    const [rows, setRows] = useState(() => data);
    const [page, setPage] = useState(0);
    const [columns] = useState(columnsArray);
    const [topBar] = useState(search);
    const [totalRowsCount, setTotalRowsCount] = useState(data.length);
    const DEFAULT_ORDER = 'asc';
    const DEFAULT_ORDER_BY = 'calories';
    const DEFAULT_ROWS_PER_PAGE = 5;
    const [candidateCvId, setCandidateCvId] = useState(0);
    const [candidateId, setCandidateId] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [order] = useState(DEFAULT_ORDER);
    const [orderBy] = useState(DEFAULT_ORDER_BY);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [displayedRowsCount, setDisplayedRowsCount] = useState(Math.min((page + 1) * rowsPerPage, data.length));
    const [visibleRows, setVisibleRows] = useState(data);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [key, setKey] = useState(0);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const [filteredIds] = useState([]);
    const [totalPages, setTotalPages] = useState(0);

    const handleRowsPerPageChange = (event) => {
        const newRowsPerPage = event.target.value;
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        const startIndex = page * newRowsPerPage;
        const endIndex = startIndex + newRowsPerPage;
        setVisibleRows(rows.slice(startIndex, endIndex));
    };

    const handleChange = (event, value) => {
        const newPage = value - 1;
        const startIndex = newPage * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;

        const sortedRows = stableSort(rows, getComparator(order, orderBy));

        if (filter === 'All') {
            const rowsOnPage = sortedRows.slice(startIndex, endIndex);
            setVisibleRows(rowsOnPage);
        } else {
            const filteredRows = sortedRows.filter((row) => {
                const department = row.hasOwnProperty('department') &&
                    row.department !== null && row.department !== undefined;
                return department && row.department.toLowerCase().includes(filter.toLowerCase());
            });
            const rowsOnPage = filteredRows.slice(startIndex, endIndex);
            setVisibleRows(rowsOnPage);
        }

        setPage(newPage);
    };

    useEffect(() => {
        const sortedRows = stableSort(rows, getComparator(order, orderBy));
        const filteredRows = filter === 'All'
            ? sortedRows
            : sortedRows.filter((row) => {
                const department =
                    row.hasOwnProperty('department') &&
                    row.department !== null &&
                    row.department !== undefined;
                return department && row.department.toLowerCase().includes(filter.toLowerCase());
            });

        const newTotalPages = Math.ceil(filteredRows.length / rowsPerPage);
        setTotalPages(newTotalPages);
        const rowsOnPage = filteredRows.slice(0, rowsPerPage);
        setVisibleRows(rowsOnPage);
    }, [rows, filter, order, orderBy, rowsPerPage]);

    const hasPopOver = columns.some(column => column.id === 'popOver');

    const tableHeaderStyle = {
        backgroundColor: '#DDDFDF',
        height: '56px',
    };

    const StyledTableCell = styled(TableCell)(({theme}) => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: "#DDDFDF",
            color: theme.palette.common.black,
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 15,
        },
    }));

    const handleClose = () => {
        setAnchorEl(null);
    };

    const requestSearch = (searchedVal) => {
        const filteredRows = rows.filter((row) => {
            const hasFullName = row.hasOwnProperty('fullName') && row.fullName !== null && row.fullName !== undefined;
            return (hasFullName && row.fullName.toLowerCase().includes(searchedVal.toLowerCase()))
        });
        const limitedRows = filteredRows.slice(0, rowsPerPage);
        setVisibleRows(limitedRows);
    };

    const filtering = useCallback(() => {
        const sortedRows = stableSort(rows, getComparator(order, orderBy));
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;

        const rowsOnPage = sortedRows.slice(startIndex, endIndex);

        setTotalRowsCount(rows.length);
        setDisplayedRowsCount(Math.min((page + 1) * rowsPerPage, rows.length))

        const filteredRows = sortedRows.filter((row) => {
            const department = row.hasOwnProperty('department') && row.department !== null && row.department !== undefined;
            return (department && row.department.toLowerCase().includes(filter.toLowerCase()));
        });

        if (filter === "All") {
            setVisibleRows(rowsOnPage)
        } else if (filter != null) {
            setVisibleRows(filteredRows.slice(startIndex, endIndex))
        }

    }, [rows, filter, order, orderBy, page, rowsPerPage])

    useEffect(() => {
        setRows(data);
    }, [data]);

    useEffect(() => {
        const sortedRows = stableSort(rows, getComparator(order, orderBy));
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;

        const rowsOnPage = sortedRows.slice(startIndex, endIndex);
        setVisibleRows(rowsOnPage);
    }, [rows, order, orderBy, page, rowsPerPage, filter]);

    useEffect(() => {
        filtering();
    }, [filter, rows, order, orderBy, page, filtering]);

    const handleOptionsClick = (e, candidateCvId, candidateId) => {
        setAnchorEl(e.currentTarget)
        setCandidateId(candidateId);
        setCandidateCvId(candidateCvId);
    }

    const handleDownloadClick = async () => {
        try {

            const cvData = await findById(candidateCvId, keycloak.token);
            if (cvData) {
                setCandidateCvId(cvData.cvID);
                const cvDocumentId = cvData.s3Key;
                const {fileName, extension} = getFileNameAndExtension(cvDocumentId);
                const blob = await getCvDocument(cvDocumentId, keycloak.token);
                const url = window.URL.createObjectURL(blob);

                // Create a link element to trigger the download
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank'; // Open in new tab
                link.setAttribute('download', `${fileName}.${extension}`); // Set the default filename for download
                document.body.appendChild(link);
                link.click();

                // Clean up
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                console.log('No CV data found.');
            }
        } catch (error) {
            console.error('Error fetching PDF URL:', error);
        }
    }

    function getFileNameAndExtension(cvDocumentId) {
        const parts = cvDocumentId.split('_');
        const nameWithExtension = parts.slice(1).join('_');
        const fileName = nameWithExtension.split('.').slice(0, -1).join('.');
        const extension = nameWithExtension.split('.').pop();
        return {fileName, extension};
    }

    const deleteClick = () => {
        openModal()
    }

    const useStyles = makeStyles((theme) => ({
        center: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        root: {
            "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                    borderColor: '#FF6600',
                    borderWidth: 0,
                }
            }
        },
        select: {
            "&.MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                    borderColor: '#FF6600'
                }
            }
        },
    }));

    const deleteCandidateByCVId = (id) => {
        deleteCVById(id, keycloak.token)
            .then(() => {
                closeModal();
                showSaveSuccessMessage("Candidate successfully deleted");
                setKey(key + 1);
                filteredIds.push(id);

                const updatedRows = rows.filter(item => !filteredIds.includes(item.candidateID));
                setRows(updatedRows);
                updatedCandidates(updatedRows);

                const newTotalPages = Math.ceil(updatedRows.length / rowsPerPage);
                setTotalPages(newTotalPages);

                setPage(prevPage => (prevPage >= newTotalPages ? newTotalPages - 1 : prevPage));

                const startIndex = (page >= newTotalPages ? newTotalPages - 1 : page) * rowsPerPage;
                const endIndex = startIndex + rowsPerPage;
                const newVisibleRows = updatedRows.slice(startIndex, endIndex);

                setVisibleRows(newVisibleRows);
                setTotalRowsCount(updatedRows.length);
                setDisplayedRowsCount(Math.min((page + 1) * rowsPerPage, updatedRows.length));
            })
            .catch(error => {
                console.error('Error deleting candidate:', error);
            });
    };

    const closeModal = () => {
        handleClose();
        setShowModal(false);
    };

    const openModal = () => {
        setShowModal(true);
    };

    const classes = useStyles();

    return (
        <div>
            {topBar ? <TopTableSearch rowsPerPage={rowsPerPage} handleRowsPerPageChange={handleRowsPerPageChange}
                                      requestSearch={requestSearch} classes={classes}/>
                : null}

            <Box id="showClientsPaper" data-testid='showClientsPaper'>
                <TableContainer sx={{borderRadius: "4px", boxShadow: "0px 0px 0px 1px #E0E0E0"}} key={key}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow style={tableHeaderStyle}>
                                {columns.map((column) => (
                                    column.id === "popOver"
                                        ? (
                                            <StyledTableCell
                                                key={column.id}
                                                align={"center"}
                                                style={{minWidth: column.minWidth}}>
                                                {column.label}
                                            </StyledTableCell>) : (<StyledTableCell
                                            key={column.id}
                                            style={{minWidth: column.minWidth}}>
                                            {column.label}
                                        </StyledTableCell>)))}
                            </TableRow>
                        </TableHead>
                        <TableBody data-testid='tableBody'>
                            {visibleRows.filter(item => !filteredIds.includes(item.candidateID)).map((dataRow, rowIndex) => {
                                const isLastRow = rowIndex === columns.length - 1;
                                return (
                                    <TableRow key={rowIndex}
                                              sx={{borderBottom: isLastRow ? '20px solid #F6F6F6' : '5px solid white'}}
                                              style={rowIndex % 2 ? {background: "rgba(248, 248, 248, 0.50)"} : {background: "#FFFFFF"}}>
                                        {columns.map((column) => (
                                            column.id === 'popOver' || column.id === 'viewButton' ? (
                                                hasPopOver ? (
                                                    <TableCell align="center" style={{borderRadius: '1px'}}>
                                                        <div>
                                                            <Button variant="text" onClick={(event) => {
                                                                handleOptionsClick(event, dataRow.cvID, dataRow.candidateID)
                                                            }} data-testid={`options-${rowIndex}`}
                                                                    style={{color: 'black', textAlign: 'center'}}>
                                                                <MoreVertOutlinedIcon/>
                                                            </Button>
                                                        </div>
                                                    </TableCell>

                                                ) : (
                                                    <TableCell align="center" style={{borderRadius: '1px'}}>
                                                        <Button key={column.id} style={{
                                                            backgroundColor: '#FF6600',
                                                            fontFamily: 'Poppins',
                                                            width: '110px',
                                                            height: '25px',
                                                            color: '#FFFFFF',
                                                            borderRadius: '5px',
                                                            display: 'flex',
                                                        }}
                                                                href={`/Candidates?jobID=${dataRow.hashedCode}`}
                                                        >View</Button>
                                                    </TableCell>
                                                )
                                            ) : <TableCell key={column.id} align="left">
                                                {column.id === 'status' ?
                                                    dataRow[column.id].description ? dataRow[column.id].description : 'Unknown Status'
                                                    :
                                                    (column.id === "appliedOn" ?
                                                            (new Date(dataRow.appliedOn).toLocaleString('en-GB', {
                                                                year: 'numeric',
                                                                month: 'numeric',
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            }))
                                                            :
                                                            dataRow[column.id]
                                                    )

                                                }
                                            </TableCell>
                                        ))}
                                    </TableRow>)
                            })}
                        </TableBody>
                    </Table>
                    <FlexContainer>
                        <LeftBottomFlexContainer>
                            <InputLabel id="display-rows-label" sx={{marginLeft: '10px', marginRight: '10px'}}>
                                Showing
                            </InputLabel>
                            <div>1 to {displayedRowsCount} of {totalRowsCount}</div>
                            <InputLabel id="display-rows-label" sx={{marginLeft: '10px'}}>
                                entries
                            </InputLabel>
                        </LeftBottomFlexContainer>

                        <FooterFlexContainer>
                            <TableFooter className={'ticketsPagination'} spacing={2}>
                                <Pagination data-testid={"paginator"} count={totalPages} page={page + 1}
                                            onChange={handleChange}/>
                            </TableFooter>
                        </FooterFlexContainer>
                    </FlexContainer>
                </TableContainer>
                <Popover id={id} open={open} anchorEl={anchorEl} onClose={handleClose}
                         anchorOrigin={{vertical: 'bottom', horizontal: 'right'}} elevation={1}>
                    <Typography sx={typographyStyles}>
                        <Button href={`/CandidateView?candidateCvId=${candidateCvId}&candidateId=${candidateId}`}
                                sx={buttonStyles} data-testid={"viewCandidateAction"}>View</Button>
                    </Typography>
                    <Typography sx={typographyStyles}>
                        <Button onClick={handleDownloadClick} sx={buttonStyles}
                                data-testid={"downloadPDFAction"}>Download</Button>
                    </Typography>
                    <Typography sx={typographyStyles}>
                        <Button onClick={() => {
                            deleteClick()
                        }} sx={buttonStyles} data-testid={"option-delete"}>Delete</Button>
                    </Typography>
                </Popover>
            </Box>
            <Modal data-testid={"confirmDelete-modal"}
                   open={showModal}
                   aria-labelledby="modal-modal-title"
                   aria-describedby="modal-modal-description">
                <Box sx={Enums.modalStyle}>
                    <h3>Delete Candidate</h3>
                    <Grid container direction={"row"} alignItems={"flex-end"} padding={2} spacing={2}>
                        <Grid item xs={12}>
                            <Typography>Are you sure you want to delete this candidate?</Typography>
                        </Grid>
                        <Grid item xs={10}>
                            <Button variant="outlined" data-testid={"cancel"} onClick={closeModal}
                                    style={{
                                        float: 'right',
                                        width: '89px',
                                        height: '40px',
                                        fontFamily: 'Poppins',
                                        color: '#FF6600',
                                        borderColor: '#FF6600'
                                    }}>Cancel</Button>
                        </Grid>
                        <Grid item xs={2}>
                            <Button onClick={() => deleteCandidateByCVId(candidateId)} data-testid={"delete"}
                                    sx={modalButtonStyleDelete}>Delete</Button>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>
        </div>
    )
}